import React, { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigationType } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, Protected } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import { ConfirmReplyTo, Forgot, Login, Register, Reset, Verify } from "@/pages/AuthPages";
import { Privacy, Terms } from "@/pages/Legal";
import DashboardLayout from "@/dashboard/Layout";
import Overview from "@/dashboard/Overview";
import Services from "@/dashboard/Services";
import Appearance from "@/dashboard/Appearance";
import Install from "@/dashboard/Install";
import Leads from "@/dashboard/Leads";
import LeadDetail from "@/dashboard/LeadDetail";
import FieldQuotes from "@/dashboard/FieldQuotes";
import FieldQuoteEditor from "@/dashboard/FieldQuoteEditor";
import Billing from "@/dashboard/Billing";
import Business from "@/dashboard/Business";
import AdminGate from "@/admin/AdminGate";
import AdminOverview from "@/admin/AdminOverview";
import AdminCompanies from "@/admin/AdminCompanies";
import AdminCompany from "@/admin/AdminCompany";
import { AdminAudit, AdminSettings } from "@/admin/AdminSettings";
import { EmbedPage, HostedPage } from "@/widget/WidgetApp";

const SITE = "Instant Quote Wizard";
const TITLES: [RegExp, string][] = [
  [/^\/$/, `${SITE} | Instant Estimates for Home Services`],
  [/^\/login/, "Login"], [/^\/register/, "Create Account"], [/^\/forgot-password/, "Forgot Password"],
  [/^\/reset-password/, "Reset Password"], [/^\/verify-email/, "Verify Email"], [/^\/confirm-reply-to/, "Confirm Reply-To"],
  [/^\/terms/, "Terms of Service"], [/^\/privacy/, "Privacy Policy"],
  [/^\/app\/?$/, "Dashboard"], [/^\/app\/leads\/./, "Lead Details"], [/^\/app\/leads/, "Leads"],
  [/^\/app\/field-quotes\/new/, "New Field Quote"], [/^\/app\/field-quotes\/./, "Field Quote"], [/^\/app\/field-quotes/, "Field Quotes"],
  [/^\/app\/services/, "Services & Pricing"], [/^\/app\/appearance/, "Widget Appearance"], [/^\/app\/install/, "Install"],
  [/^\/app\/billing/, "Billing"], [/^\/app\/business/, "Business Profile"], [/^\/app\/admin/, "Super Admin"],
];

// New pages open at the top; back/forward keeps the browser's own position. Contractor widget pages set their own title.
function RouteScroll() {
  const { pathname } = useLocation();
  const type = useNavigationType();
  useEffect(() => { if (type !== "POP") window.scrollTo(0, 0); }, [pathname, type]);
  useEffect(() => {
    const t = TITLES.find(([re]) => re.test(pathname));
    if (t) document.title = t[1].includes("|") ? t[1] : `${t[1]} | ${SITE}`;
    else if (!/^\/(q|embed)\//.test(pathname)) document.title = `${SITE} | Instant Estimates for Home Services`;
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <RouteScroll />
      <Routes>
        <Route path="/embed/:publicId" element={<EmbedPage />} />
        <Route path="/q/:slug" element={<HostedPage />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<AuthedApp />} />
      </Routes>
      <Toaster richColors position="top-center" />
    </BrowserRouter>
  );
}

function AuthedApp() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<Forgot />} />
        <Route path="/reset-password" element={<Reset />} />
        <Route path="/verify-email" element={<Verify />} />
        <Route path="/confirm-reply-to" element={<ConfirmReplyTo />} />
        <Route path="/app" element={<Protected><DashboardLayout /></Protected>}>
          <Route index element={<Overview />} />
          <Route path="services" element={<Services />} />
          <Route path="appearance" element={<Appearance />} />
          <Route path="install" element={<Install />} />
          <Route path="leads" element={<Leads />} />
          <Route path="leads/:id" element={<LeadDetail />} />
          <Route path="field-quotes" element={<FieldQuotes />} />
          <Route path="field-quotes/new" element={<FieldQuoteEditor key="new" />} />
          <Route path="field-quotes/:id" element={<FieldQuoteEditor />} />
          <Route path="billing" element={<Billing />} />
          <Route path="business" element={<Business />} />
          <Route path="admin" element={<AdminGate />}>
            <Route index element={<AdminOverview />} />
            <Route path="companies" element={<AdminCompanies />} />
            <Route path="companies/:id" element={<AdminCompany />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="audit" element={<AdminAudit />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
